import type {
  IPublicClientApplication,
  AccountInfo,
} from "@azure/msal-browser";
import { loginRequest, redirectRequest } from "../config/authConfig";
import { config } from "../application/common/static-config";

export class AuthService {
  private msalInstance: IPublicClientApplication;

  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  /**
   * Login using redirect flow for all devices (simplified approach)
   * No popups - always use redirect for better reliability
   */
  async login() {
    try {
      // Check if MSAL interaction is already in progress
      const interactionInProgress =
        this.msalInstance.getActiveAccount() !== null &&
        this.msalInstance.getAllAccounts().length === 0;

      if (interactionInProgress) {
        console.log(
          "AuthService: MSAL interaction already in progress, skipping login"
        );
        return null;
      }

      // Check if login is already in progress to prevent redirect loops
      const loginInProgress = sessionStorage.getItem("login-in-progress");
      const authCompleted = sessionStorage.getItem("auth-completed");

      // Enhanced checks for browser compatibility
      if (loginInProgress === "true") {
        const startTime = sessionStorage.getItem("login-start-time");
        if (startTime) {
          const elapsed = Date.now() - parseInt(startTime);
          if (elapsed < 60000) {
            // Less than 1 minute ago
            console.log("AuthService: Login already in progress, skipping");
            return null;
          } else {
            console.log("AuthService: Clearing stale login progress flags");
            sessionStorage.removeItem("login-in-progress");
            sessionStorage.removeItem("login-start-time");
          }
        }
      }

      // Check if auth was recently completed (browser-specific timing issues)
      if (authCompleted === "true") {
        const completedTime = sessionStorage.getItem("auth-completed-time");
        if (completedTime) {
          const elapsed = Date.now() - parseInt(completedTime);
          if (elapsed < 5000) {
            // Less than 5 seconds ago
            console.log("AuthService: Auth recently completed, skipping login");
            return null;
          }
        }
      }

      // Check if user is already authenticated
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        console.log("AuthService: User already authenticated, skipping login");
        return null;
      }

      // Mark login as in progress
      sessionStorage.setItem("login-in-progress", "true");
      sessionStorage.setItem("login-start-time", Date.now().toString());
      sessionStorage.removeItem("auth-completed"); // Clear any previous completion flag

      // Always use redirect flow for both mobile and desktop
      sessionStorage.setItem("auth-method", "redirect-only");

      console.log("AuthService: Starting login redirect");
      await this.msalInstance.loginRedirect(redirectRequest);
      // Note: loginRedirect doesn't return a response immediately
      // The response will be handled by handleRedirectPromise in the main app
      return null;
    } catch (error) {
      console.error("AuthService: Login failed:", error);
      // Clear login in progress flag on error
      sessionStorage.removeItem("login-in-progress");
      sessionStorage.removeItem("login-start-time");

      // If it's an interaction in progress error, don't throw it
      if (
        error instanceof Error &&
        error.message.includes("interaction_in_progress")
      ) {
        console.log(
          "AuthService: Interaction already in progress, handled gracefully"
        );
        return null;
      }

      throw error;
    }
  }

  /**
   * Get access token silently
   */
  async getAccessToken() {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error("No accounts found");
    }

    const request = {
      ...loginRequest,
      account: accounts[0],
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      console.error("Failed to acquire token silently:", error);

      // Handle interaction_in_progress errors gracefully
      if (
        error instanceof Error &&
        error.message.includes("interaction_in_progress")
      ) {
        console.log(
          "AuthService: Interaction in progress, waiting for completion"
        );
        throw new Error("Authentication interaction in progress, please wait");
      }

      // If silent token acquisition fails, use redirect for all devices (no popups)
      try {
        await this.msalInstance.acquireTokenRedirect(request);
        throw new Error(
          "Token acquisition redirect initiated, please wait for redirect completion"
        );
      } catch (interactiveError) {
        console.error(
          "Failed to acquire token interactively:",
          interactiveError
        );

        // Handle interaction_in_progress errors for interactive flow as well
        if (
          interactiveError instanceof Error &&
          interactiveError.message.includes("interaction_in_progress")
        ) {
          throw new Error(
            "Authentication interaction in progress, please wait"
          );
        }

        throw interactiveError;
      }
    }
  }

  /**
   * Get ID token (contains user claims)
   */
  async getIdToken() {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error("No accounts found");
    }

    const request = {
      ...loginRequest,
      account: accounts[0],
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(request);
      return response.idToken;
    } catch (error) {
      console.error("Failed to acquire ID token silently:", error);

      // Handle interaction_in_progress errors gracefully
      if (
        error instanceof Error &&
        error.message.includes("interaction_in_progress")
      ) {
        console.log(
          "AuthService: Interaction in progress for ID token, waiting for completion"
        );
        throw new Error("Authentication interaction in progress, please wait");
      }

      // If silent token acquisition fails, use redirect for all devices (no popups)
      try {
        await this.msalInstance.acquireTokenRedirect(request);
        throw new Error(
          "ID token acquisition redirect initiated, please wait for redirect completion"
        );
      } catch (interactiveError) {
        console.error(
          "Failed to acquire ID token interactively:",
          interactiveError
        );

        // Handle interaction_in_progress errors for interactive flow as well
        if (
          interactiveError instanceof Error &&
          interactiveError.message.includes("interaction_in_progress")
        ) {
          throw new Error(
            "Authentication interaction in progress, please wait"
          );
        }

        throw interactiveError;
      }
    }
  }

  /**
   * Exchange Azure AD token for backend JWT token
   */
  async exchangeTokenWithBackend(): Promise<string> {
    try {
      // Check if API base URL is configured
      const apiBaseUrl = config.baseURL;
      if (!apiBaseUrl) {
        throw new Error(
          "Backend API URL not configured (REACT_APP_API_BASE_URL missing)"
        );
      }

      // Try with ID token first (contains user claims like email)
      let azureToken: string;
      try {
        azureToken = await this.getIdToken();
      } catch {
        azureToken = await this.getAccessToken();
      }

      const apiUrl = `${apiBaseUrl}/auth/token`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${azureToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 500) {
          // Check if backend is unavailable
          const backendUnavailable =
            sessionStorage.getItem("backend-unavailable") === "true";

          if (backendUnavailable) {
            throw new Error(
              "Backend authentication unavailable - continuing with Azure authentication"
            );
          }

          // Parse the error response to handle specific error cases
          let errorData: { message?: string; detail?: string } | string = {};
          try {
            const errorText = await response.text();
            // Try to parse as JSON first
            try {
              errorData = JSON.parse(errorText);
            } catch {
              // If not JSON, treat the text as the error message
              errorData = { message: errorText };
            }
          } catch {
            // If we can't parse the response, treat as generic 401
            errorData = {
              message: "Authentication failed: User not authorized",
            };
          }

          console.log("AuthService: Backend auth error:", errorData);

          // Type guard to check if errorData is an object
          const isErrorObject = (
            data: unknown
          ): data is { message?: string; detail?: string } => {
            return typeof data === "object" && data !== null;
          };

          // Handle specific backend error messages
          const errorMessage =
            typeof errorData === "string"
              ? errorData
              : isErrorObject(errorData)
              ? errorData.message || ""
              : "";
          const errorDetail = isErrorObject(errorData)
            ? errorData.detail || ""
            : "";

          if (
            errorDetail.includes("Failed to find user by email") ||
            errorMessage.includes("User does not exist in the database") ||
            (typeof errorData === "string" &&
              errorData.includes("User does not exist in the database"))
          ) {
            console.log("AuthService: Creating UserNotFoundError...");
            const userNotFoundError = new Error(
              "User account not found in database. Please contact your administrator to add your account."
            );
            userNotFoundError.name = "UserNotFoundError";
            console.log(
              "AuthService: Throwing UserNotFoundError:",
              userNotFoundError
            );
            throw userNotFoundError;
          }

          if (
            errorMessage.includes("User account is inactive") ||
            (typeof errorData === "string" &&
              errorData.includes("User account is inactive"))
          ) {
            const userInactiveError = new Error(
              "User account is inactive. Please contact your administrator."
            );
            userInactiveError.name = "UserInactiveError";
            throw userInactiveError;
          }

          // Only trigger logout if we have a valid Azure session but backend rejects it
          // This prevents auto-logout due to temporary backend issues
          const hasValidAzureSession =
            this.msalInstance.getAllAccounts().length > 0;

          if (!hasValidAzureSession) {
            // Clear all storage
            localStorage.clear();
            sessionStorage.clear();

            // Redirect to login page
            window.location.href = "/login";
          } else {
            console.warn(
              "AuthService: Azure session valid but backend rejected token - attempting token refresh"
            );
            // Try to get a fresh token instead of logging out immediately
            const backendErrorMessage =
              typeof errorData === "string"
                ? errorData
                : isErrorObject(errorData)
                ? errorData.message || "User not authorized"
                : "User not authorized";
            throw new Error(
              `Backend authentication failed: ${backendErrorMessage}`
            );
          }

          const finalErrorMessage =
            typeof errorData === "string"
              ? errorData
              : isErrorObject(errorData)
              ? errorData.message ||
                "User not active or insufficient permissions"
              : "User not active or insufficient permissions";
          throw new Error(`Authentication failed: ${finalErrorMessage}`);
        }

        const errorText = await response.text();
        throw new Error(
          `Token exchange failed: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data = await response.json();
      return data.token || data.accessToken;
    } catch (error) {
      console.error("AuthService: Token exchange failed:", error);

      // Provide more specific error messages
      if (error instanceof TypeError && error.message.includes("fetch")) {
        const networkError = new Error(
          "Backend API is not available or unreachable - network connectivity issue"
        );
        networkError.name = "NetworkError";
        throw networkError;
      }

      // Check for other network-related errors
      if (error instanceof Error) {
        if (
          error.message.includes("INTERNET_DISCONNECTED") ||
          error.message.includes("ERR_NETWORK") ||
          error.message.includes("ERR_INTERNET_DISCONNECTED")
        ) {
          const networkError = new Error(
            "Internet connection lost - backend API unreachable"
          );
          networkError.name = "NetworkError";
          throw networkError;
        }
      }

      throw error;
    }
  }

  /**
   * Get current user account
   */
  getCurrentAccount(): AccountInfo | null {
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0;
  }

  /**
   * Logout user using redirect flow (simplified approach)
   * No popups - always use redirect for better reliability
   */
  async logout() {
    try {
      const accounts = this.msalInstance.getAllAccounts();

      // Clear all local storage
      localStorage.clear();

      // Clear all session storage
      sessionStorage.clear();

      if (accounts.length > 0) {
        // Always use redirect flow for both mobile and desktop
        await this.msalInstance.logoutRedirect({
          account: accounts[0],
          postLogoutRedirectUri: config.AUTH_LOGOUT_REDIRECT_URI || window.location.origin,
        });
      }
    } catch (error) {
      console.error("Logout failed:", error);
      // Even if logout fails, still try to clear storage
      try {
        localStorage.clear();
        sessionStorage.clear();
        console.log("AuthService: Storage cleared after logout error");
      } catch (storageError) {
        console.error("AuthService: Failed to clear storage:", storageError);
      }
      throw error;
    }
  }
}
